import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../context/TimerContext';
import { useCart } from '../context/CartContext';
import { shopifyApi } from '../api/shopify';
import { CountdownTimer } from '../components/CountdownTimer';
import { CategoryNav } from '../components/CategoryNav';
import { CategorySidebar } from '../components/CategorySidebar';
import { Product, Collection } from '../types';

export function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { isSiteActive, isExpired, loading: timerLoading } = useTimer();
  const { addToCart, getItemCount } = useCart();

  const [products, setProducts] = useState<Product[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addedProductId, setAddedProductId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Get selected collection from URL or user's default
  const urlCollection = searchParams.get('category');
  const [selectedCollection, setSelectedCollection] = useState<string | null>(
    urlCollection || user?.defaultCollection || null
  );

  // Sync URL with selected collection
  useEffect(() => {
    if (selectedCollection) {
      setSearchParams({ category: selectedCollection });
    } else {
      setSearchParams({});
    }
  }, [selectedCollection, setSearchParams]);

  // Fetch collections on mount
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const fetchCollections = async () => {
      try {
        const data = await shopifyApi.getCollections();
        setCollections(data);
      } catch (err: any) {
        console.error('Failed to fetch collections:', err);
      }
    };

    fetchCollections();
  }, [authLoading, isAuthenticated]);

  // Fetch products when collection changes
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const data = await shopifyApi.getProducts(selectedCollection || undefined);
        setProducts(data);
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [authLoading, isAuthenticated, selectedCollection]);

  // Handle site expiration - only after timer has loaded
  useEffect(() => {
    if (timerLoading) {
      return;
    }

    if (isExpired || !isSiteActive) {
      logout();
      navigate('/');
    }
  }, [isExpired, isSiteActive, timerLoading, logout, navigate]);

  const handleAddToCart = (product: Product) => {
    if (product.inventoryQuantity <= 0) return;

    addToCart(product);
    setAddedProductId(product.id);

    setTimeout(() => {
      setAddedProductId(null);
    }, 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCategorySelect = (handle: string | null) => {
    setSelectedCollection(handle);
    setSearchQuery(''); // Clear search when changing category
  };

  const formatPrice = (price: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(price));
  };

  // Filter products based on search query
  const filteredProducts = products.filter((product) =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get custom title from user if available, otherwise use default
  const pageTitle = user?.customTitle || 'Jackpoint Jackstands VIP Customer Access';

  // Determine if we should show category navigation
  const showCategories = collections.length > 1;

  return (
    <div className="products-page">
      <header className="products-header">
        <div className="header-left">
          <img src="/JackpointLogo.avif" alt="Jackpoint" className="header-logo" />
          {showCategories && (
            <CategoryNav
              collections={collections}
              selected={selectedCollection}
              onSelect={handleCategorySelect}
            />
          )}
        </div>
        <div className="header-right">
          <CountdownTimer />
          <Link to="/cart" className="cart-link">
            Cart ({getItemCount()})
          </Link>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="products-layout">
        {showCategories && (
          <CategorySidebar
            collections={collections}
            selected={selectedCollection}
            onSelect={handleCategorySelect}
          />
        )}

        <main className={`products-main ${showCategories ? 'with-sidebar' : ''}`}>
          <h1 className="page-title">{pageTitle}</h1>
          {loading ? (
            <p className="loading">Loading products...</p>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : products.length === 0 ? (
            <p className="no-products">No VIP products available.</p>
          ) : (
            <>
              <div className="products-toolbar">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <a
                  href="https://jackpointjackstands.com/pages/vehicle-fitment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fitment-help-link"
                >
                  Need fitment help? Click Here
                </a>
                <span className="product-count">
                  {filteredProducts.length} of {products.length} products
                </span>
              </div>
              <div className="products-grid">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="product-card">
                    <div className="product-image-container">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.title}
                          className="product-image"
                        />
                      ) : (
                        <div className="no-image">No image</div>
                      )}
                      {product.inventoryQuantity <= 0 && (
                        <span className="product-badge sold-out">Sold out</span>
                      )}
                      {product.inventoryQuantity > 0 && product.inventoryQuantity <= 5 && (
                        <span className="product-badge low-stock">Low stock</span>
                      )}
                    </div>
                    <div className="product-info">
                      <h3 className="product-title">{product.title}</h3>
                      <p className="product-price">
                        {formatPrice(product.price, product.currencyCode)}
                      </p>
                      <div className="product-actions">
                        <Link
                          to={`/products/${product.handle}${product.collectionHandle ? `?collection=${product.collectionHandle}` : ''}`}
                          className="view-details-link"
                        >
                          Details
                        </Link>
                        <button
                          className={`add-to-cart-btn ${addedProductId === product.id ? 'added' : ''}`}
                          onClick={() => handleAddToCart(product)}
                          disabled={product.inventoryQuantity <= 0}
                        >
                          {addedProductId === product.id ? 'Added!' : product.inventoryQuantity <= 0 ? 'Sold out' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default ProductsPage;
